import React, { useRef } from "react";
import { defineBlock, smartProps, useEditor } from "@smartfire/gutenberg-react";
import { RichText } from "@wordpress/block-editor";
import { MediaUpload, MediaUploadCheck } from "@wordpress/block-editor";
import Slider from "react-slick";
import EditableText from "../shared/editable-text";
import style from "./block.module.scss";
import "slick-carousel/slick/slick.css";
import { ReactComponent as ArrowLeft } from "./arrow_left.svg";
import { ReactComponent as ArrowRight } from "./arrow_right.svg";

const ALLOWED_MEDIA_TYPES = ["image"];

function Block({ slides }) {
  const sliderRef = useRef();
  const { isEditing, updateData } = useEditor();

  const settings = {
    dots: false,
    arrows: false,
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
  };

  function updateValue(key, value, i) {
    slides[i][key] = value;
    updateData({ slides: [...slides] });
  }

  const addSlide = () => {
    let newSlide = {
      date: "",
      title: "",
      description: "",
      background: { url: "" },
    };
    updateData({ slides: [...slides, newSlide] });
  };

  const deleteSlide = (i) => {
    slides.splice(i, 1);
    updateData({ slides: [...slides] });
  };

  const SlidesAdmin = () => {
    return (
      <>
        {slides &&
          slides.map((item, i) => (
            <div className={style.slide} key={i}>
              <div className={style.contentAdmin}>
                <button
                  className={style.deleteButton}
                  onClick={() => deleteSlide(i)}
                >
                  <svg
                    aria-hidden="true"
                    role="img"
                    focusable="false"
                    class="dashicon dashicons-trash"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                  >
                    <path d="M12 4h3c.6 0 1 .4 1 1v1H3V5c0-.6.5-1 1-1h3c.2-1.1 1.3-2 2.5-2s2.3.9 2.5 2zM8 4h3c-.2-.6-.9-1-1.5-1S8.2 3.4 8 4zM4 7h11l-.9 10.1c0 .5-.5.9-1 .9H5.9c-.5 0-.9-.4-1-.9L4 7z"></path>
                  </svg>
                </button>
                <div className={style.row}>
                  <div className={style.col}>
                    <RichText
                      tagName="div"
                      placeholder={"..."}
                      formattingControls={[]}
                      value={item.date}
                      className={"editableInput"}
                      onChange={(value) => updateValue("date", value, i)}
                    />
                    <RichText
                      tagName="div"
                      placeholder={"..."}
                      formattingControls={[]}
                      value={item.title}
                      className={"editableInput"}
                      onChange={(value) => updateValue("title", value, i)}
                    />
                    <RichText
                      tagName="div"
                      placeholder={"..."}
                      formattingControls={[]}
                      value={item.description}
                      className={"editableInput"}
                      onChange={(value) => updateValue("description", value, i)}
                    />
                  </div>
                  <div className={style.col}>
                    <div
                      className={style.backgroundPreview}
                      style={{
                        backgroundImage: item.background
                          ? `url("${item.background.url}")`
                          : null,
                      }}
                    />
                    <MediaUploadCheck>
                      <MediaUpload
                        allowedTypes={ALLOWED_MEDIA_TYPES}
                        value={item.background ? item.background.id : null}
                        render={({ open }) => (
                          <button
                            className={style.backgroundImage}
                            onClick={open}
                          >
                            Modifier l'image
                          </button>
                        )}
                        onSelect={(media) =>
                          updateValue("background", media, i)
                        }
                      />
                    </MediaUploadCheck>
                  </div>
                </div>
              </div>
            </div>
          ))}
        <button className={style.addButton} onClick={() => addSlide()}>
          <svg
            aria-hidden="true"
            role="img"
            focusable="false"
            class="dashicon dashicons-insert"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
          >
            <path d="M10 1c-5 0-9 4-9 9s4 9 9 9 9-4 9-9-4-9-9-9zm0 16c-3.9 0-7-3.1-7-7s3.1-7 7-7 7 3.1 7 7-3.1 7-7 7zm1-11H9v3H6v2h3v3h2v-3h3V9h-3V6z"></path>
          </svg>
        </button>
      </>
    );
  };

  return (
    <div className={style.block}>
      <div className={isEditing ? style.contentNoGrid : style.content}>
        <EditableText className={style.title} tagName="h2" smartProp="title" />
        <div className={style.tiret} />
      </div>
      {isEditing ? (
        <>{SlidesAdmin()}</>
      ) : (
        <Slider ref={sliderRef} className={style.slider} {...settings}>
          {slides.map((item) => (
            <div className={style.slide}>
              <div
                className={style.background}
                style={{ backgroundImage: `url("${item.background.url}")` }}
              />
              <div className={style.content}>
                <div className={style.date}>{item.date}</div>
                <div className={style.whitePanel}>
                  <div
                    className={style.title}
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                  <div
                    className={style.text}
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                  <div className={style.smallDate}>{item.date}</div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      )}

      {!isEditing && (
        <div className={style.bottomContent}>
          <button
            className={style.leftArrow}
            onClick={() => sliderRef.current.slickPrev()}
          >
            <ArrowLeft />
          </button>
          <div className={style.bars} />
          <button
            className={style.rightArrow}
            onClick={() => sliderRef.current.slickNext()}
          >
            <ArrowRight />
          </button>
        </div>
      )}
    </div>
  );
}

export default defineBlock({
  Block,
  smartProps: {
    title: smartProps.string.withDefault("30 ans d'expérience"),
    slides: smartProps.any.withDefault([
      {
        date: "2011",
        title: "Focus sur les hautes technologies",
        description:
          "Nam vel neque ipsum. Mauris ac ipsum eu tortor commodo fermentum ut eu augue. Curabitur posuere tortor at ex dignissim rhoncus. Duis porttitor facilisis nibh.",
        background: {
          url: "https://lh3.googleusercontent.com/p/AF1QipO_thVeiZEvRTbwSrbjCMSeyNXBh9ScSy3iMFeh=w660-h440-c",
        },
      },
      {
        date: "2012",
        title: "Donec et augue vel libero convallis dignissim.s",
        description:
          "Mauris ac ipsum eu tortor commodo fermentum ut eu augue. Curabitur posuere tortor at ex dignissim rhoncus. Duis porttitor facilisis nibh.",
        background: {
          url: "https://img1.10bestmedia.com/Images/Photos/260555/p-Levels-Club---Sukhumvit-11_54_990x660.jpg",
        },
      },
    ]),
  },
  setup: {
    title: "Chronologie",
  },
});
